import { useWorkspaceText } from '../interface-copy';
import { useEffect, useState } from 'react';
import { useCompany } from '@/company/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import connectionCopy from '@/i18n/ns/connection-access';
import { useWorkspace } from '../context';
import { ConnectionWriteSetup } from '../connect-access/write-setup';
import { listConnectionReadAccess, type ConnectionReadItem } from '../connection-read-access/api';

// Under the agent's Uses, its owner picks one of their own current accounts.
// Writing is turned on only in Connections (D73); setup shows whether the account
// allows it and links there. Runs then follow that account's current access.

/** Choose only current authorized accounts; writing is turned on only in Connections (D73). */
export function AgentConnectionSetup() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const company = useCompany();
  const copy = useNamespaceCopy(connectionCopy);
  const scope = `${company.data?.company?.id ?? ''}:${ws.user?.id ?? ''}`;
  const [loaded, setLoaded] = useState<{
    scope: string;
    items: ConnectionReadItem[];
  } | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setLoaded(null);
    setSelectedId('');
    setError(false);
    if (company.ready && ws.user)
      void listConnectionReadAccess()
        .then(({ items }) => {
          if (active) setLoaded({ scope, items });
        })
        .catch(() => {
          if (active) setError(true);
        });
    return () => {
      active = false;
    };
  }, [scope, company.ready, revision]);
  const items = loaded?.scope === scope ? loaded.items : [];
  const selected = items.find((item) => item.connectionId === selectedId);
  return (
    <section className="ws-agents-field" aria-label={uiText('Connection write setup')}>
      <label>
        <span>{uiText('Account for connected changes')}</span>
        <select
          aria-label={uiText('Connection')}
          value={selected?.connectionId ?? ''}
          disabled={!items.length}
          onChange={(event) => setSelectedId(event.target.value)}
        >
          <option value="">{uiText('Choose a connection')}</option>
          {items.map((item) => (
            <option key={item.connectionId} value={item.connectionId}>
              {item.name}
            </option>
          ))}
        </select>
      </label>
      {error && (
        <>
          <p role="alert">{copy.saveFailed}</p>
          <button className="ws-button" onClick={() => setRevision((value) => value + 1)}>
            {uiText('Refresh')}
          </button>
        </>
      )}
      {selected && (
        <ConnectionWriteSetup
          key={`${scope}:${selected.connectionId}`}
          connection={selected}
          onAllowed={(id) => {
            if (id === selected.connectionId)
              ws.notify(formatCopy(copy.allowWriteDone, { name: selected.name }));
          }}
        />
      )}
    </section>
  );
}
